import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  countriesAPI,
  zonesAPI,
  type Country,
  type Zone,
  type ZoneCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

function getCountryName(zone: Zone, countries: Country[]) {
  if (zone.country_name) {
    return zone.country_name;
  }
  const id = zone.country;
  if (id === null) {
    return "—";
  }
  const c = countries.find(p => String(p.id) === String(id));
  return c?.name ?? String(id);
}

export default function ZonePage() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ZoneCreatePayload>({
    name: "",
    country: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<Zone | null>(null);
  const [deleting, setDeleting] = useState<Zone | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchCountries = async () => {
    try {
      const list = await countriesAPI.list();
      setCountries(Array.isArray(list) ? list : []);
    } catch {
      setCountries([]);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await zonesAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setZones(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load zones");
      setZones([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchCountries();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Zone name is required");
      return;
    }
    if (!form.country) {
      toast.error("Please select a country");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: Number(form.country) || form.country,
        is_active: form.is_active,
      };
      if (editing) {
        await zonesAPI.update(editing.id, payload);
        toast.success("Zone updated successfully");
      } else {
        await zonesAPI.create(payload);
        toast.success("Zone created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", country: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save zone";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openEdit = useCallback(
    (z: Zone) => {
      setEditing(z);
      setForm({
        name: z.name,
        country:
          z.country !== null
            ? String(z.country)
            : countries[0]?.id !== null
              ? String(countries[0].id)
              : "",
        is_active: z.is_active ?? true,
      });
      setDialogOpen(true);
    },
    [countries]
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<Zone>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "country",
            label: "Country",
            width: 160,
            renderCell: row => getCountryName(row, countries),
          },
        ],
      }),
    [currentPage, pageSize, openEdit, countries]
  );

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await zonesAPI.delete(deleting.id);
      toast.success("Zone deleted successfully");
      setDeleting(null);
      if (zones.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete zone";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Zone</h1>
        <p className="text-muted-foreground">Manage zones</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-zone"
        tableColumns={tableColumns}
        listTitle={`Zones (${totalItems})`}
        searchPlaceholder="Search zones..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", country: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    country:
                      countries[0]?.id !== null ? String(countries[0].id) : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit Zone" : "Create New Zone"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Country *</Label>
                  <Select
                    value={String(form.country ?? "")}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, country: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zone-name">Name *</Label>
                  <Input
                    id="zone-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Zone name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="zone-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="zone-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-zone"
            columns={tableColumns}
            data={zones}
            getRowKey={z => z.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No zones yet. Create one to get started."
                : "No zones found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Zone</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
